import {
  Button,
  Flex,
  Heading,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  Stack,
  Text,
  Tooltip,
  useBreakpointValue,
  useDisclosure,
} from '@chakra-ui/react'
import { faClock, faDoorOpen, faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import chakraTokenToVar from '@/utils/chakraTokenToVar'
import { noOp } from '@/utils/helpers'

import useAPIWrapper from '@/client/hooks/useAPIWrapper'
import RPC from '@/client/rpc'
import type { AgeAssuranceSessionConfig } from '@/client/services/ageAssurance/types'
import { getAgeAssuranceVideoAskUrl, getYotiSessionUrl, WEBSITE_URL } from '@/client/services/url'

import { useToaster } from '@/components/Toast/useToaster'
import MenuItem from '@/components/UI/MenuItem'
import ModalV3 from '@/components/UI/Modal/ModalV3'
import ShwLogo from '@/components/UI/ShwLogo'

export type AgeAssuranceSessionModalResult = 'completed' | 'aborted' | 'later'

const SHOW_TOOLTIP_DELAY_MILLIS = 500
const HIDE_TOOLTIP_DELAY_MILLIS = 4500
export type AgeAssuranceSessionModalProps = {
  sessionConfig: AgeAssuranceSessionConfig
  isOpen: boolean
  onDone: (result: AgeAssuranceSessionModalResult) => void
}

export default function AgeAssuranceSessionModal({
  sessionConfig,
  isOpen,
  onDone,
}: AgeAssuranceSessionModalProps) {
  const toast = useToaster()
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const {
    isOpen: isVerifyLaterOpen,
    onClose: onVerifyLaterClose,
    onOpen: onVerifyLaterOpen,
  } = useDisclosure()
  const { isOpen: isIssueOpen, onClose: onIssueClose, onOpen: onIssueOpen } = useDisclosure()

  const [showExitTooltip, setShowExitTooltip] = useState(false)

  useEffect(() => {
    if (isOpen && isMobile) {
      const showTimeout = setTimeout(() => {
        setShowExitTooltip(true)
      }, SHOW_TOOLTIP_DELAY_MILLIS)
      const hideTimeout = setTimeout(() => {
        setShowExitTooltip(false)
      }, HIDE_TOOLTIP_DELAY_MILLIS)
      return () => {
        clearTimeout(showTimeout)
        clearTimeout(hideTimeout)
      }
    }
    setShowExitTooltip(false)
    return noOp
  }, [isOpen, isMobile])

  const iframeUrl = useMemo(
    () =>
      sessionConfig.method === 'yoti'
        ? getYotiSessionUrl({ sessionId: sessionConfig.sessionId })
        : getAgeAssuranceVideoAskUrl(),
    [sessionConfig]
  )

  const handleLoad = useCallback(() => {
    const iframe = iframeRef.current
    if (iframe == null) {
      return
    }

    // If the iframe redirects to our site, then the age assurance
    // session completed successfully.
    try {
      const currentUrl = iframe.contentWindow?.location.href
      if (currentUrl != null && currentUrl.includes(WEBSITE_URL)) {
        onDone('completed')
      }
    } catch {
      // Cross-origin access throws - expected when iframe is on external domain
    }
  }, [onDone])

  const [reportVerifyLater, isReportingVerifyLater] = useAPIWrapper(
    async () => {
      await RPC.reportAgeAssuranceVerifyLater({})
      onDone('later')
      onVerifyLaterClose()
    },
    [onDone, onVerifyLaterClose],
    'Report Age Assurance Verify Later'
  )

  const [maybeAbortSession] = useAPIWrapper(
    async () => {
      const { aborted } = await RPC.maybeAbortAgeAssuranceSession({})
      if (aborted) {
        toast({
          description: 'You have aborted your age verification session',
          status: 'success',
        })
      }
      onDone('aborted')
    },
    [toast, onDone],
    'Abort Age Assurance Session'
  )

  if (iframeUrl == null) {
    return null
  }

  return (
    <>
      <ModalV3
        title={() => (
          <Flex align="center" gap="12px">
            <ShwLogo height="24px" />
            <Heading size="lg" as="h4">
              Age Verification
            </Heading>
          </Flex>
        )}
        size="full"
        open={isOpen}
        removeBodyPaddingAndMargin
        disableClose
        onClose={noOp}
        body={() => (
          <iframe
            ref={iframeRef}
            key={iframeUrl}
            title="Age Verification"
            src={iframeUrl}
            onLoad={handleLoad}
            style={{
              width: '100%',
              flex: 1,
              borderTop: `1px solid ${chakraTokenToVar('border')}`,
              borderBottom: `1px solid ${chakraTokenToVar('border')}`,
              borderLeft: 'none',
              borderRight: 'none',
            }}
            allow="camera; microphone"
          />
        )}
        rightHeaderContent={() => (
          <Flex gap="8px">
            {isMobile ? (
              <Menu onOpen={() => setShowExitTooltip(false)}>
                <Tooltip
                  label="Tap here to exit or report an issue"
                  isOpen={showExitTooltip}
                  hasArrow
                  placement="bottom-end"
                  fontSize="14px"
                  padding="8px 12px"
                >
                  <MenuButton
                    as={IconButton}
                    variant="ghost"
                    aria-label="Options"
                    icon={
                      <FontAwesomeIcon icon={faDoorOpen} color={chakraTokenToVar('fg')} size="xl" />
                    }
                    onClick={() => setShowExitTooltip(false)}
                  />
                </Tooltip>
                <MenuList border={`2px solid ${chakraTokenToVar('border.emphasized')}`}>
                  <MenuItem
                    value="verify-later"
                    icon={faClock}
                    label="Verify later"
                    onClick={onVerifyLaterOpen}
                  />
                  <MenuItem
                    value="issue"
                    icon={faTriangleExclamation}
                    color="error.fg"
                    label="There was an issue"
                    onClick={onIssueOpen}
                  />
                </MenuList>
              </Menu>
            ) : (
              <>
                <Button
                  variant="outline"
                  leftIcon={<FontAwesomeIcon icon={faClock} />}
                  onClick={onVerifyLaterOpen}
                >
                  Verify Later
                </Button>
                {sessionConfig.method === 'yoti' ? (
                  <Button
                    variant="danger-outline"
                    leftIcon={<FontAwesomeIcon icon={faTriangleExclamation} />}
                    onClick={onIssueOpen}
                  >
                    There was an issue
                  </Button>
                ) : null}
              </>
            )}
          </Flex>
        )}
      />
      <ModalV3
        title="Verify Later"
        open={isVerifyLaterOpen}
        onClose={onVerifyLaterClose}
        body={() => (
          <Stack gap="16px">
            <Text>
              You won&apos;t be able to join Zoom sessions or complete a tutor application until you
              finish age verification.
            </Text>
            <Text>You can come back and verify anytime from your Settings page.</Text>
            <Text fontWeight="semibold">Do you want to verify later?</Text>
          </Stack>
        )}
        rightFooterContent={() => (
          <Flex
            gap="8px"
            width="100%"
            justifyContent="flex-end"
            flexDirection={{ base: 'column-reverse', sm: 'row' }}
          >
            <Button
              variant="outline"
              width={{ base: '100%', sm: 'auto' }}
              onClick={onVerifyLaterClose}
            >
              Continue Verification
            </Button>
            <Button
              width={{ base: '100%', sm: 'auto' }}
              isLoading={isReportingVerifyLater}
              onClick={() => {
                reportVerifyLater().catch(noOp)
              }}
            >
              Verify Later
            </Button>
          </Flex>
        )}
      />
      <ModalV3
        title="There Was an Issue"
        open={isIssueOpen}
        onClose={onIssueClose}
        body={() => (
          <Stack gap="16px">
            <Text>
              If you&apos;re unable to complete verification because of a technical problem, you can
              submit a manual video for review. Manual reviews take up to 2 business days.
            </Text>
            <Text>If you can complete verification normally, please select Try Again.</Text>
          </Stack>
        )}
        rightFooterContent={() => (
          <Flex
            gap="8px"
            width="100%"
            justifyContent="flex-end"
            flexDirection={{ base: 'column-reverse', sm: 'row' }}
          >
            <Button variant="outline" width={{ base: '100%', sm: 'auto' }} onClick={onIssueClose}>
              Try Again
            </Button>
            <Button
              width={{ base: '100%', sm: 'auto' }}
              onClick={() => {
                onIssueClose()
                maybeAbortSession().catch(noOp)
              }}
            >
              Continue to Manual Verification
            </Button>
          </Flex>
        )}
      />
    </>
  )
}
